import { Box, Drawer, Stack, useMediaQuery, useTheme } from '@mui/material'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '../../store/redux/hooks'
import { getSidebarState, setSidebarOpen } from '../../store/redux/reducers/sidebarSlice'
import { CollapsibleSectionConfig, SidebarLinkItem } from '../../store/types/components/sidebar/sidebarTypes'
import SidebarCollapsedContent from './SidebarCollapsedContent'
import SidebarExpandedContent from './SidebarExpandedContent'
import SidebarFooter from './SidebarFooter'
import SidebarHeader from './SidebarHeader'
import { useSidebarIndicator } from './hooks/useSidebarIndicator'
import { useSidebarLinks } from './hooks/useSidebarLinks'
import { useSidebarLogout } from './hooks/useSidebarLogout'
import { useSidebarSections } from './hooks/useSidebarSections'
import { SIDEBAR_COLLAPSED_WIDTH, SIDEBAR_WIDTH } from './sidebarConstants'
import { SIDEBAR_SECTION_DEFINITIONS } from './sidebarSectionConfig'

const Sidebar = () => {
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('smClose'))
  const { t } = useTranslation()
  const { logout } = useSidebarLogout()
  const { openSections, toggleSection } = useSidebarSections()
  const { linksBySection } = useSidebarLinks()

  const open = useSelector(getSidebarState)
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(setSidebarOpen(!isSmallScreen))
  }, [dispatch, isSmallScreen])

  const sections: CollapsibleSectionConfig[] = useMemo(
    () =>
      SIDEBAR_SECTION_DEFINITIONS.flatMap(def => {
        const links = linksBySection[def.key] ?? []
        if (links.length === 0) return []

        return [
          {
            key: def.key,
            icon: def.icon,
            label: t(def.labelKey),
            ariaLabelOpen: def.ariaLabelOpenKey ? t(def.ariaLabelOpenKey) : undefined,
            ariaLabelClose: def.ariaLabelCloseKey ? t(def.ariaLabelCloseKey) : undefined,
            open: openSections[def.key],
            links,
            centerContentWhenCollapsed: def.centerContentWhenCollapsed,
            disableToggleWhenSidebarCollapsed: def.disableToggleWhenSidebarCollapsed,
          },
        ]
      }),
    [linksBySection, openSections, t]
  )

  const sidebarLinks: SidebarLinkItem[] = useMemo(
    () =>
      sections.flatMap(section =>
        section.links.map(link => ({
          link,
          section: section.key,
          isOpen: section.open,
        }))
      ),
    [sections]
  )

  const visibleLinks = useMemo(() => sidebarLinks.filter(item => item.isOpen), [sidebarLinks])

  const { linkRefs, indicatorRef, isCollapsing, handleCollapseStart, handleCollapseComplete } = useSidebarIndicator(
    sidebarLinks,
    visibleLinks,
    open
  )

  return (
    <Drawer
      variant="permanent"
      open={open}
      PaperProps={{
        elevation: 4,
        sx: {
          backgroundImage: 'none',
          backgroundColor: 'background.elevation1',
          color: 'text.mediumEmphasis',
          borderWidth: 0,
          pb: 2,
          px: open ? 3 : 1,
          pt: 3.5,
          overflowX: 'hidden',
          position: 'fixed',
          width: open ? SIDEBAR_WIDTH : SIDEBAR_COLLAPSED_WIDTH,
          bottom: 0,
          alignItems: open ? 'none' : 'center',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        },
        tabIndex: -1,
      }}
    >
      <Stack
        direction="column"
        justifyContent="space-between"
        sx={{ height: '100%', overflowY: 'hidden', width: '100%' }}
      >
        <SidebarHeader open={open} />

        <Box
          sx={{
            width: '100%',
            overflowY: 'auto',
            overflowX: 'hidden',
            scrollbarGutter: 'stable',
            flexGrow: 1,
            pr: open ? 1 : 0,
            mt: 2,
          }}
        >
          {open ? (
            <SidebarExpandedContent
              sections={sections}
              visibleLinksCount={visibleLinks.length}
              isCollapsing={isCollapsing}
              indicatorRef={indicatorRef}
              linkRefs={linkRefs}
              onToggleSection={toggleSection}
              onCollapseStart={handleCollapseStart}
              onCollapseComplete={handleCollapseComplete}
            />
          ) : (
            <SidebarCollapsedContent sections={sections} />
          )}
        </Box>

        <SidebarFooter open={open} onLogout={logout} />
      </Stack>
    </Drawer>
  )
}

export default Sidebar
